import React, {FC, useContext} from 'react';

import {withLocalization} from 'epic-localization/src/context/LocalizationContext';

import {SidebarPreviousDataContext} from '../SidebarPreviousDataProvider';
import StatisticsPanel from '../StatisticsPanel';
import StatisticsCard from '../StatisticsCard';
import useMyOverallHighlightsData from './useMyOverallHighlightsData';
import StatisticsCardContent from '../StatisticsCardContent';
import MyOverallHighlightsTooltip from './MyOverallHighlightsTooltip';
import MyOverallHighlightTrend from './MyOverallHighlightTrend';
import DataUnavailableTooltip from '../DataUnavailableTooltip';
import {MyOverallHighlight} from '../../../types';
import {PDFPictureTrap} from '../../v2/sections/fortnite-games/export/PDFExportProvider';

const CODES = {
    [MyOverallHighlight.UNIQUE_PLAYERS]: {
        title: 'epic.affiliate.fortnite_games.sidebar.my_overal_hignlights.total_unique_players',
        description:
            'epic.affiliate.fortnite_games.sidebar.my_overal_hignlights.total_trend_description'
    },
    [MyOverallHighlight.MONTHLY]: {
        title: 'epic.affiliate.fortnite_games.sidebar.my_overal_hignlights.monthly_players',
        description:
            'epic.affiliate.fortnite_games.sidebar.my_overal_hignlights.monthly_trend_description'
    },
    [MyOverallHighlight.WEEKLY]: {
        title: 'epic.affiliate.fortnite_games.sidebar.my_overal_hignlights.weekly_players',
        description:
            'epic.affiliate.fortnite_games.sidebar.my_overal_hignlights.weekly_trend_description'
    },
    [MyOverallHighlight.LIFETIME_PLAYS]: {
        title: 'epic.affiliate.fortnite_games.sidebar.my_overal_hignlights.total_lifetime_plays',
        description:
            'epic.affiliate.fortnite_games.sidebar.my_overal_hignlights.total_trend_description'
    },
    title: 'epic.affiliate.fortnite_games.sidebar.my_overal_hignlights.title',
    tooltipTitle: 'epic.affiliate.fortnite_games.sidebar.my_overal_hignlights.tooltip_title',
    tooltipDescription:
        'epic.affiliate.fortnite_games.sidebar.my_overal_hignlights.tooltip_description',
    dataUnavailableTitle: 'epic.affiliate.fortnite_games.sidebar.data_unavailable_title',
    dataUnavailableDescription: 'epic.affiliate.fortnite_games.sidebar.data_unavailable_description'
};

export interface MyOverallHighlightsPanelProps {
    getMessage: (code: string) => string;
}

const MyOverallHighlightsPanel: FC<MyOverallHighlightsPanelProps> = ({
    getMessage
}: MyOverallHighlightsPanelProps) => {
    const {
        totals: {uniquePlayers, lifetimePlays, loading, error},
        monthly,
        weekly
    } = useMyOverallHighlightsData();

    const {prevUniquePlayers, prevLifetimePlays, prevMonthly, prevWeekly} = useContext(
        SidebarPreviousDataContext
    );

    const previousData = {
        [MyOverallHighlight.UNIQUE_PLAYERS]: prevUniquePlayers,
        [MyOverallHighlight.MONTHLY]: prevMonthly,
        [MyOverallHighlight.WEEKLY]: prevWeekly,
        [MyOverallHighlight.LIFETIME_PLAYS]: prevLifetimePlays
    };

    const games = {
        uniquePlayers: {...uniquePlayers, loading, error},
        monthly,
        weekly,
        lifetimePlays: {...lifetimePlays, loading, error}
    };

    return (
        <PDFPictureTrap id={getMessage(CODES.title)} index={5}>
            <StatisticsPanel
                title={getMessage(CODES.title)}
                tooltip={
                    <MyOverallHighlightsTooltip
                        title={getMessage(CODES.tooltipTitle)}
                        description={getMessage(CODES.tooltipDescription)}
                    />
                }>
                {Object.entries(games).map(([game, {amount, trend, loading}]) => (
                    <StatisticsCard
                        key={game}
                        title={getMessage(CODES[game].title)}
                        value={
                            <StatisticsCardContent
                                previousValue={previousData[game].amount}
                                value={amount}
                                title={getMessage(CODES.dataUnavailableTitle)}
                                tooltip={
                                    !amount && (
                                        <DataUnavailableTooltip
                                            title={getMessage(CODES.dataUnavailableTitle)}
                                            description={getMessage(
                                                CODES.dataUnavailableDescription
                                            )}
                                        />
                                    )
                                }
                            />
                        }
                        trend={<MyOverallHighlightTrend value={trend} />}
                        description={getMessage(CODES[game].description)}
                        isLoading={loading}
                    />
                ))}
            </StatisticsPanel>
        </PDFPictureTrap>
    );
};

export default withLocalization(MyOverallHighlightsPanel);
